import * as React from 'react'
import { historyState } from '../../../store/historyState.ts'
import { useSnapshot, ref } from 'valtio'
import { dayHistoryState, resetDayHistory } from '../../../store/dayHistoryState.ts'
import { controlState } from '../../../store/controlState.ts'
import { dataState } from '../../../store/dataState.ts'
import { deviceFromPet } from '../../../helpers/dataHelpers.ts'
import { fetchDayHistory } from '../../../helpers/dayHistoryHelper.ts'
import { DateTime } from 'luxon'
import { effectiveTimezone } from '../../../store/authState.ts'

export const HistoryToggles: React.FC = () => {
    const showSafeZones = (useSnapshot(historyState) as typeof historyState).showSafeZones
    const includeInvalid = (useSnapshot(historyState) as typeof historyState).includeInvalid
    const isMobile = useSnapshot(controlState).isOnMobileDevice
    const dayHistoryActive = (useSnapshot(dayHistoryState) as typeof dayHistoryState).active
    const dayHistoryLoading = (useSnapshot(dayHistoryState) as typeof dayHistoryState).loading

    const toggleDayHistory = async () => {
        const store = dayHistoryState

        if (store.active) {
            // Exit day history mode
            resetDayHistory()
            return
        }

        // Enter day history mode
        const selectedPetId = historyState.selectedPetId
        if (!selectedPetId) return

        const pets = dataState.pets
        const pet = pets.find(p => p._id === selectedPetId)
        if (!pet) return

        const device = deviceFromPet(pet)
        if (!device) return

        store.active = true
        store.loading = true

        // Use today in the user's timezone as the starting point for Day History.
        // We don't use historyStore.endDate because it's in the browser's local timezone
        // and zone-converting it can shift to the wrong calendar day.
        const tz = effectiveTimezone()
        const zone = tz.value
        const date = DateTime.now().setZone(zone)

        store.selectedDate = ref(date) as unknown as DateTime
        const data = await fetchDayHistory(device.iccid, pet._id, date)

        if (data) {
            store.pathData = data
            store.error = ''
        } else {
            store.error = 'Failed to load day history'
        }
        store.loading = false
    }

    return (
        <div className={'flex items-center gap-3'}>
            {/* Mode segmented control — desktop only */}
            {!isMobile && (
                <div className={'flex rounded-lg overflow-hidden border border-primary/30'}>
                    <button
                        className={`without-ring px-3 py-1 text-xs font-semibold transition-colors ${!dayHistoryActive ? 'bg-primary text-primary-content' : 'bg-base-100 text-primary hover:bg-primary/10'}`}
                        onClick={() => { if (dayHistoryActive) resetDayHistory() }}
                        disabled={dayHistoryLoading}
                    >
                        All Reports
                    </button>
                    <button
                        className={`without-ring px-3 py-1 text-xs font-semibold transition-colors ${dayHistoryActive ? 'bg-primary text-primary-content' : 'bg-base-100 text-primary hover:bg-primary/10'}`}
                        onClick={() => { if (!dayHistoryActive) void toggleDayHistory() }}
                        disabled={dayHistoryLoading}
                    >
                        Daily Movement
                    </button>
                </div>
            )}

            {/* Toggles — always visible */}
            <label className={'flex items-center gap-1.5 cursor-pointer'} onClick={() => historyState.showSafeZones = !showSafeZones}>
                <span className={'text-xs font-medium text-primary'}>Safe Zones</span>
                <div className={`relative w-8 h-4 rounded-full transition-colors ${showSafeZones ? 'bg-primary' : 'bg-gray-300'}`}>
                    <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all ${showSafeZones ? 'left-[18px]' : 'left-0.5'}`} />
                </div>
            </label>
            {!isMobile && (
                <label className={`flex items-center gap-1.5 ${dayHistoryActive ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`} onClick={() => !dayHistoryActive && (historyState.includeInvalid = !includeInvalid)}>
                    <span className={'text-xs font-medium text-primary'}>Valid GPS Only</span>
                    <div className={`relative w-8 h-4 rounded-full transition-colors ${!includeInvalid ? 'bg-primary' : 'bg-gray-300'}`}>
                        <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all ${!includeInvalid ? 'left-[18px]' : 'left-0.5'}`} />
                    </div>
                </label>
            )}
        </div>
    )
}
